import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Modal,
  TextInput,
  Select,
  DatePicker,
  Alert,
  Badge,
  Button,
  Mail01,
  Phone01,
  Building07,
} from '@opendate-app/setlist';
import useAutoSave from '../../shared/components/useAutoSave';
import CollapsibleSection from '../../shared/components/CollapsibleSection';
import CrmCustomFieldsSection from '../../shared/components/CrmCustomFieldsSection';
import SidebarTagsPicker from '../../shared/components/SidebarTagsPicker';
import { COUNTRY_OPTIONS, getRegionsForCountry, getRegionLabelForCountry } from '../../CustomFieldValues/constants/customFieldValuesConstants';
import { canSubmitCustomFieldValue } from '../../CustomFieldValues/actions/customFieldValuesActionCreators';
import useCustomFieldValues from '../../shared/hooks/useCustomFieldValues';
import crmLabels from '../../shared/utils/crmLabels';

const CONTACT_DETAILS_ICON = '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>';
const SOCIAL_LINK_ICON = '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>';
const NOTES_ICON = '<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>';
const SECONDARY_CONTACT_ICON = '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>';

const ORG_MENU_STYLE = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 8,
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: 8,
  boxShadow: '0 12px 16px -4px rgba(10,13,18,0.08), 0 4px 6px -2px rgba(10,13,18,0.03)',
  padding: '6px 0',
  backgroundColor: 'var(--sl-bg-primary)',
  zIndex: 1000,
  maxHeight: 320,
  overflowY: 'auto',
};

const ORG_HINT_STYLE = { padding: '6px 12px', color: 'var(--sl-text-quaternary)', fontSize: 13 };

function ContactFormModal({
  isOpen,
  onClose,
  contact,
  team,
  csrfToken,
  customFieldDefinitions = [],
  contactTypes = [],
  constituentIdsEnabled = false,
  constituentIdLabel = null,
  onSaved,
}) {
  const isEditing = !!(contact && contact.id);
  const attachedArchivedTypes = (contact?.contact_types || []).filter((type) => !contactTypes.some((active) => active.id === type.id));
  const typeOptions = [...contactTypes, ...attachedArchivedTypes].map((type) => ({ value: type.id, label: type.archived ? `${type.name} (archived)` : type.name }));

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [contactTypeIds, setContactTypeIds] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [birthday, setBirthday] = useState('');
  const [emailSecondary1, setEmailSecondary1] = useState('');
  const [emailSecondary2, setEmailSecondary2] = useState('');
  const [phoneSecondary1, setPhoneSecondary1] = useState('');
  const [constituentId, setConstituentId] = useState('');
  const [notes, setNotes] = useState('');
  const { customFieldValues, handleCustomFieldChange, buildCustomFieldAttributes, resetCustomFieldValues, syncCustomFieldRowIds } = useCustomFieldValues(customFieldDefinitions, contact ? contact.custom_field_values : []);
  const [taggedItems, setTaggedItems] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const pendingTagOps = useRef([]);

  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgResults, setOrgResults] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const orgSearchTimerRef = useRef(null);
  const orgRef = useRef(null);

  const handleSaved = useCallback(async (saved, isNew) => {
    syncCustomFieldRowIds(saved.custom_field_values);
    if (isNew && pendingTagOps.current.length > 0) {
      try {
        if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        for (const op of pendingTagOps.current) {
          if (op.type === 'add') {
            await axios.post(`/teams/${team.id}/tagged_items`, {
              tagged_item: { taggable_id: saved.id, taggable_type: 'Fan', tag_id: op.tagId },
            });
          } else if (op.type === 'create') {
            await axios.post(`/teams/${team.id}/tagged_items`, {
              tagged_item: {
                taggable_id: saved.id,
                taggable_type: 'Fan',
                tag_attributes: { name: op.tagName },
              },
            });
          }
        }
        pendingTagOps.current = [];
      } catch (err) {
        toast.error('Failed to save tags.');
      }
    }
    if (onSaved) onSaved(saved, isNew);
  }, [onSaved, team, csrfToken]);

  const { recordId, saveStatus, errors, saveFields } = useAutoSave({
    team,
    csrfToken,
    resourcePath: 'crm/fans',
    resourceKey: 'fan',
    existingId: isEditing ? contact.id : null,
    isOpen,
    onSaved: handleSaved,
  });

  useEffect(() => {
    if (!isOpen) return;

    // The modal stays mounted across open/close; re-init custom field state so
    // multi-association row ids don't go stale after a save.
    resetCustomFieldValues(contact && contact.id ? contact.custom_field_values : []);

    if (contact && contact.id) {
      setFirstName(contact.first_name || '');
      setLastName(contact.last_name || '');
      setEmail(contact.email || '');
      setPhone(contact.phone || contact.phone_number || '');
      setAddress1(contact.address1 || '');
      setAddress2(contact.address2 || '');
      setCity(contact.city || '');
      setState(contact.state || '');
      setZip(contact.zip || '');
      setCountry(contact.country || '');
      setContactTypeIds((contact.contact_types || []).map((type) => type.id));
      setCompanyName(contact.company_name || '');
      setFacebookUrl(contact.facebook_url || '');
      setInstagramUrl(contact.instagram_url || '');
      setTwitterUrl(contact.twitter_url || '');
      setLinkedinUrl(contact.linkedin_url || '');
      setBirthday(contact.birthday || '');
      setEmailSecondary1(contact.email_secondary_1 || '');
      setEmailSecondary2(contact.email_secondary_2 || '');
      setPhoneSecondary1(contact.phone_secondary_1 || '');
      setConstituentId(contact.constituent_id || '');
      setNotes('');
      setTaggedItems([]);
      setAvailableTags([]);
      pendingTagOps.current = [];
    } else {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setAddress1('');
      setAddress2('');
      setCity('');
      setState('');
      setZip('');
      setCountry('');
      setContactTypeIds([]);
      setCompanyName('');
      setFacebookUrl('');
      setInstagramUrl('');
      setTwitterUrl('');
      setLinkedinUrl('');
      setBirthday('');
      setEmailSecondary1('');
      setEmailSecondary2('');
      setPhoneSecondary1('');
      setConstituentId('');
      setNotes('');
      setTaggedItems([]);
      setAvailableTags([]);
      pendingTagOps.current = [];
    }
  }, [isOpen, contact]);

  useEffect(() => {
    if (!orgDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (orgRef.current && !orgRef.current.contains(e.target)) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [orgDropdownOpen]);

  const contactId = isEditing ? contact.id : recordId;

  const refreshTags = useCallback(async (id) => {
    if (!team) return;
    try {
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const requests = [
        axios.get(`/teams/${team.id}/crm/tags`, { params: { scope: 'Fan' } }),
      ];
      if (id) {
        requests.push(axios.get(`/teams/${team.id}/tagged_items`, {
          params: { taggable_type: 'Fan', taggable_id: id },
        }));
      }
      const responses = await Promise.all(requests);
      setAvailableTags(responses[0].data || []);
      if (responses[1]) setTaggedItems(responses[1].data || []);
    } catch (err) {
      toast.error('Failed to load tags.');
    }
  }, [team, csrfToken]);

  useEffect(() => {
    if (isOpen) refreshTags(contactId);
  }, [isOpen, contactId, refreshTags]);

  const handleAddTag = async (tag) => {
    if (!contactId) {
      pendingTagOps.current.push({ type: 'add', tagId: tag.id });
      setTaggedItems((prev) => [...prev, { id: `pending-${tag.id}`, tag }]);
      return;
    }
    try {
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.post(`/teams/${team.id}/tagged_items`, {
        tagged_item: { taggable_id: contactId, taggable_type: 'Fan', tag_id: tag.id },
      });
      refreshTags(contactId);
    } catch (err) {
      toast.error('Failed to add tag.');
    }
  };

  const handleRemoveTag = async (taggedItemId) => {
    if (String(taggedItemId).startsWith('pending-')) {
      const tagId = parseInt(String(taggedItemId).replace('pending-', ''), 10);
      pendingTagOps.current = pendingTagOps.current.filter((op) => !(op.type === 'add' && op.tagId === tagId));
      setTaggedItems((prev) => prev.filter((item) => item.id !== taggedItemId));
      return;
    }
    try {
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${team.id}/tagged_items/${taggedItemId}`);
      refreshTags(contactId);
    } catch (err) {
      toast.error('Failed to remove tag.');
    }
  };

  const handleCreateTag = async (tagName) => {
    if (!tagName.trim()) return;
    if (!contactId) {
      pendingTagOps.current.push({ type: 'create', tagName: tagName.trim() });
      setTaggedItems((prev) => [...prev, { id: `pending-new-${tagName}`, tag: { id: `new-${tagName}`, name: tagName.trim() } }]);
      return;
    }
    try {
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.post(`/teams/${team.id}/tagged_items`, {
        tagged_item: {
          taggable_id: contactId,
          taggable_type: 'Fan',
          tag_attributes: { name: tagName.trim() },
        },
      });
      refreshTags(contactId);
    } catch (err) {
      toast.error('Failed to create tag.');
    }
  };

  if (!isOpen) return null;

  const hasAddress = address1.trim().length > 0 && (zip.trim().length > 0 || (city.trim().length > 0 && state.trim().length > 0));
  const canBeReached = email.trim().length > 0 || phone.trim().length > 0 || hasAddress;
  const hasName = [firstName, lastName, companyName].some((value) => value.trim().length > 0);
  const showConstituentId = !!constituentIdLabel && (constituentIdsEnabled || !!contact?.constituent_id);
  const canSubmitForm = hasName && canBeReached && customFieldValues.every((cfv) => canSubmitCustomFieldValue(cfv));

  const buildPayload = () => ({
    first_name: firstName,
    last_name: lastName,
    email: email.trim() || null,
    phone_number: phone,
    address1,
    address2,
    city,
    state,
    zip,
    country,
    contact_type_ids: contactTypeIds,
    company_name: companyName,
    facebook_url: facebookUrl,
    instagram_url: instagramUrl,
    twitter_url: twitterUrl,
    linkedin_url: linkedinUrl,
    birthday,
    email_secondary_1: emailSecondary1,
    email_secondary_2: emailSecondary2,
    phone_secondary_1: phoneSecondary1,
    custom_field_values_attributes: buildCustomFieldAttributes(),
    ...(showConstituentId && constituentId.trim() !== (contact?.constituent_id || '') ? { constituent_id: constituentId.trim() || null } : {}),
  });

  const handleClose = () => {
    if (saveStatus !== 'saving') onClose();
  };

  const handleSubmit = () => {
    if (!canSubmitForm || saveStatus === 'saving') return;
    saveFields(buildPayload(), { afterSave: onClose });
  };

  const searchOrganizations = (query) => {
    clearTimeout(orgSearchTimerRef.current);
    if (!query.trim()) { setOrgResults([]); return; }
    orgSearchTimerRef.current = setTimeout(async () => {
      try {
        setOrgLoading(true);
        if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        const res = await axios.get(`/teams/${team.id}/crm/organizations.json`, {
          params: { 'q[name_cont]': query.trim(), per_page: 10 },
        });
        setOrgResults(res.data.organizations || []);
      } catch (e) {
        setOrgResults([]);
      } finally {
        setOrgLoading(false);
      }
    }, 250);
  };

  const handleSelectOrg = (org) => {
    setCompanyName(org.name);
    setOrgDropdownOpen(false);
    setOrgSearch('');
    setOrgResults([]);
  };

  const saveStatusLabel = {
    idle: null,
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Error saving',
  }[saveStatus];

  const saveStatusColor = {
    saving: 'gray',
    saved: 'success',
    error: 'error',
  }[saveStatus] || 'gray';

  const contactName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
  const labels = crmLabels(team);
  const breadcrumbLabel = contactName || (isEditing ? `Edit ${labels.contact.singular}` : `Add ${labels.contact.singular}`);

  const regionLabel = getRegionLabelForCountry(country || 'US');

  const birthdayValue = birthday ? new Date(`${birthday}T00:00:00`) : null;
  const handleBirthdayChange = (date) => {
    const val = (date instanceof Date && !Number.isNaN(date.getTime()))
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      : '';
    setBirthday(val);
    if (isEditing || recordId) {
      saveFields({ ...buildPayload(), birthday: val });
    }
  };

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
      {saveStatusLabel && <Badge color={saveStatusColor}>{saveStatusLabel}</Badge>}
      <Button
        variant="primary"
        disabled={!canSubmitForm || saveStatus === 'saving'}
        onClick={handleSubmit}
      >
        {saveStatus === 'saving' ? 'Saving…' : `Save ${labels.contact.singular}`}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={handleClose}
      icon={false}
      layout="stacked"
      width={920}
      title={breadcrumbLabel}
      footer={footer}
    >
      {errors.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Alert color="error" title="Please fix the following" description={errors.join(', ')} />
        </div>
      )}

      <div className="cfm-body" style={{ maxHeight: '68vh' }}>
        <div className="cfm-pane-main">
          <div className="cfm-field-row-2col">
            <div className="cfm-field">
              <TextInput
                label="First name"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="cfm-field">
              <TextInput
                label="Last name"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="cfm-field">
            <TextInput
              label="Email"
              type="email"
              leadingIcon={Mail01}
              placeholder="olivia@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {showConstituentId && (
            <div className="cfm-field">
              <TextInput
                label={constituentIdLabel}
                maxLength={50}
                value={constituentId}
                onChange={(e) => setConstituentId(e.target.value)}
                hint={isEditing ? 'Permanent ID for this record. Change it only to correct a mistake.' : 'Leave blank to assign the next ID automatically.'}
              />
            </div>
          )}

          <div className="cfm-field" style={{ position: 'relative' }} ref={orgRef}>
            <label className="cfm-label">{labels.organization.singular}</label>
            <TextInput
              leadingIcon={Building07}
              placeholder={`Search ${labels.organization.singular.toLowerCase()}…`}
              value={orgDropdownOpen ? orgSearch : companyName}
              onChange={(e) => {
                setOrgSearch(e.target.value);
                searchOrganizations(e.target.value);
              }}
              onFocus={() => {
                setOrgDropdownOpen(true);
                setOrgSearch(companyName);
                if (companyName) searchOrganizations(companyName);
              }}
            />
            {orgDropdownOpen && (
              <div style={ORG_MENU_STYLE}>
                {orgLoading && <div style={ORG_HINT_STYLE}>Searching…</div>}
                {!orgLoading && orgSearch.trim() && orgResults.length === 0 && (
                  <div style={ORG_HINT_STYLE}>No results</div>
                )}
                {companyName && (
                  <button
                    type="button"
                    className="cfm-contact-option"
                    style={{ color: 'var(--sl-text-quaternary)' }}
                    onMouseDown={() => { setCompanyName(''); setOrgDropdownOpen(false); setOrgSearch(''); setOrgResults([]); }}
                  >
                    Clear
                  </button>
                )}
                {orgResults.map((org) => (
                  <button
                    key={org.id}
                    type="button"
                    className="cfm-contact-option"
                    onMouseDown={() => handleSelectOrg(org)}
                  >
                    <span className="cfm-contact-name">{org.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <CollapsibleSection title="Contact Details" icon={CONTACT_DETAILS_ICON} defaultExpanded={false}>
            <div className="cfm-field">
              <TextInput
                label="Phone number"
                type="tel"
                leadingIcon={Phone01}
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="cfm-field-row-2col">
              <div className="cfm-field">
                <TextInput
                  label="Address 1"
                  placeholder="Street address"
                  value={address1}
                  onChange={(e) => setAddress1(e.target.value)}
                />
              </div>
              <div className="cfm-field">
                <TextInput
                  label="Address 2"
                  placeholder="Apt, suite, etc."
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                />
              </div>
            </div>

            <div className="cfm-field">
              <label className="cfm-label">Country</label>
              <Select
                aria-label="Country"
                placeholder="Country"
                clearable
                options={COUNTRY_OPTIONS.map((c) => ({ value: c.code, label: c.name }))}
                value={country || null}
                onChange={(value) => { setCountry(value || ''); setState(''); }}
              />
            </div>

            <div className="cfm-field-row-3col">
              <div className="cfm-field">
                <TextInput
                  label="City"
                  placeholder="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div className="cfm-field">
                <label className="cfm-label">{regionLabel}</label>
                <Select
                  aria-label={regionLabel}
                  placeholder={regionLabel}
                  clearable
                  options={getRegionsForCountry(country || 'US').map((r) => ({ value: r.code, label: r.name }))}
                  value={state || null}
                  onChange={(value) => setState(value || '')}
                />
              </div>
              <div className="cfm-field">
                <TextInput
                  label="Zip"
                  placeholder="Zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </div>
            </div>

            <div className="cfm-field">
              <DatePicker
                label="Birthday"
                placeholder="Select date"
                value={birthdayValue}
                onChange={handleBirthdayChange}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Secondary Contact Details" icon={SECONDARY_CONTACT_ICON} defaultExpanded={false}>
            <div className="cfm-field">
              <TextInput
                label="Secondary Email 1"
                type="email"
                placeholder="secondary@example.com"
                value={emailSecondary1}
                onChange={(e) => setEmailSecondary1(e.target.value)}
              />
            </div>
            <div className="cfm-field">
              <TextInput
                label="Secondary Email 2"
                type="email"
                placeholder="secondary2@example.com"
                value={emailSecondary2}
                onChange={(e) => setEmailSecondary2(e.target.value)}
              />
            </div>
            <div className="cfm-field">
              <TextInput
                label="Secondary Phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phoneSecondary1}
                onChange={(e) => setPhoneSecondary1(e.target.value)}
              />
            </div>
          </CollapsibleSection>

          <CrmCustomFieldsSection
            team={team}
            customFieldDefinitions={customFieldDefinitions}
            customFieldValues={customFieldValues}
            onCustomFieldChange={handleCustomFieldChange}
          />

          <CollapsibleSection title="Social Links" icon={SOCIAL_LINK_ICON} defaultExpanded={false}>
            <div className="cfm-social-grid">
              <div className="cfm-field">
                <TextInput
                  label="Facebook"
                  type="url"
                  placeholder="facebook.com/username"
                  value={facebookUrl}
                  onChange={(e) => setFacebookUrl(e.target.value)}
                />
              </div>
              <div className="cfm-field">
                <TextInput
                  label="Instagram"
                  type="url"
                  placeholder="instagram.com/username"
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                />
              </div>
              <div className="cfm-field">
                <TextInput
                  label="X / Twitter"
                  type="url"
                  placeholder="x.com/username"
                  value={twitterUrl}
                  onChange={(e) => setTwitterUrl(e.target.value)}
                />
              </div>
              <div className="cfm-field">
                <TextInput
                  label="LinkedIn"
                  type="url"
                  placeholder="linkedin.com/in/username"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(e.target.value)}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Notes" icon={NOTES_ICON} defaultExpanded={false}>
            <div className="cfm-field">
              <TextInput
                label="Notes"
                multiline
                rows={3}
                placeholder="Add notes about this contact…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </CollapsibleSection>
        </div>

        <div className="cfm-pane-sidebar">
          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Type</span>
            <Select
              size="sm"
              aria-label="Type"
              placeholder="Select…"
              multiple
              searchable
              clearable
              options={typeOptions}
              value={contactTypeIds}
              onChange={(value) => setContactTypeIds(value || [])}
            />
          </div>

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Source</span>
            <Select size="sm" aria-label="Source" disabled options={[{ value: 'manual', label: 'Manual' }]} value="manual" />
          </div>

          <div className="cfm-prop-row" style={{ alignItems: 'flex-start' }}>
            <span className="cfm-prop-label" style={{ paddingTop: 4 }}>Tags</span>
            <SidebarTagsPicker
              taggedItems={taggedItems}
              availableTags={availableTags}
              onAdd={handleAddTag}
              onRemove={handleRemoveTag}
              onCreateTag={handleCreateTag}
            />
          </div>

          <div className="cfm-sidebar-divider" />
          <div className="cfm-sidebar-section-label">Record Info</div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Created</span>
            <span className="cfm-prop-value-static">
              {contact && contact.created_at
                ? new Date(contact.created_at).toLocaleDateString()
                : 'Now'}
            </span>
          </div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Created by</span>
            <span className="cfm-prop-value-static">You</span>
          </div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Modified</span>
            <span className="cfm-prop-value-static">
              {contact && contact.updated_at
                ? new Date(contact.updated_at).toLocaleDateString()
                : 'Now'}
            </span>
          </div>

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Status</span>
            <Select size="sm" aria-label="Status" disabled options={[{ value: 'active', label: 'Active' }]} value="active" />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default ContactFormModal;
